import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FormGroup, Label, Button } from "reactstrap";
import Select from "react-select";
import axios from "../../../../axios";
import "./FilterResults.css";
import * as url from "../../../../utils/url";
import DatePickerMulti from "react-multi-date-picker";
import debounce from "lodash.debounce";
import { fieldOptions } from "../../../../utils/constants.js";
import { isMobile } from "../../../../utils/deviceInfo";

const FilterResults = ({
  setFilterResultsValues,
  type,
  setClearFilters,
  setIsFiltersAvailable,
  publishedDate,
  currentTrivia
}) => {
  const [columnData, setColumnData] = useState([]);
  const [typesArray, setTypesArray] = useState({})
  const [selectedValues, setSelectedValues] = useState({
    user: {},
    question: {},
    insights: {},
  });
  const [lastAppliedValues, setLastAppliedValues] = useState({
    user: {},
    question: {},
    insights: {},
  });
  const [sentiment, setSentiment] = useState({ label: "All", value: "ALL" });
  const [needsAttention, setNeedsAttention] = useState(false);
  const [defaultOptionsForFields, setDefaultOptionsForFields] = useState({});
  const [dynamicOptionsForFields, setDynamicOptionsForFields] = useState({});
  const [loadingForFields, setLoadingForFields] = useState({});
  const [loading, setLoading] = useState(true);

  // Messages are always text-based so filters are always enabled.
  // For surveys, only enable if there's at least one TEXT question.
  const hasTextQuestion = currentTrivia && (
    currentTrivia.isMessaging ||
    (currentTrivia.questions && currentTrivia.questions.some((q) => q.question_type === "TEXT"))
  );

  useEffect(() => {
    if (!publishedDate) return;

    const handler = setTimeout(() => {
      fetchData(publishedDate);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [publishedDate]);

  useEffect(() => {
    // Reset local state when switching tabs (type change)
    setSentiment({ label: "All", value: "ALL" });
    setNeedsAttention(false);

    // Reset selected values for the new type to be safe and start fresh
    if (columnData.length > 0) {
      const cleared = {};
      columnData.forEach((col) => {
        cleared[col.field] = col.type === "number" ? { start: "", end: "" } : [];
      });
      setSelectedValues((prev) => ({
        ...prev,
        [type]: cleared,
      }));
      setLastAppliedValues((prev) => ({
        ...prev,
        [type]: {},
      }));
    }
  }, [type]);

  const areFiltersEqual = (a, b, columnMap) => {
    const keysA = Object.keys(a || {});
    const keysB = Object.keys(b || {});
    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      const valA = a[key];
      const valB = b[key] || {};

      if (columnMap[key] && columnMap[key].type === "number") {
        if (valA.start !== valB.start || valA.end !== valB.end) {
          return false;
        }
      } else {
        const arrA = Array.isArray(valA) ? valA : [];
        const arrB = Array.isArray(valB) ? valB : [];
        if (arrA.length !== arrB.length) return false;

        for (let i = 0; i < arrA.length; i++) {
          const itemA = arrA[i] instanceof Date ? arrA[i].toISOString() : arrA[i];
          const itemB = arrB[i] instanceof Date ? arrB[i].toISOString() : arrB[i];
          if (itemA !== itemB) return false;
        }
      }
    }
    return true;
  };

  const fetchData = async (date) => {
    axios
      .get(
        `${url.getOrgUserMetaData}?publishedDate=${encodeURIComponent(date)}`
      )
      .then(({ data }) => {
        const meta = data && data.data && data.data.data;
        if (!meta || !meta.typeInfo) return;


        setIsFiltersAvailable(true);
        let tempDate = [];
        let tempNum = [];

        const dropdowns = meta.typeInfo.map((col) => {
          if (col.isValidFormat) {
            tempDate.push(col.fieldName)
          } else if (col.type === 'number') {
            tempNum.push(col.fieldName)
          }
          return ({
            field: col.fieldName,
            options: (meta.columnMetadata[col.fieldName] || []).map((value) => ({
              label: value,
              value: value,
            })),
            isActive: col.isActive,
            isValidFormat: col.isValidFormat,
            type: col.type,
          })
        });

        setTypesArray({
          dateArray: tempDate,
          numberArray: tempNum
        })
        const initialSelected = {
          user: {},
          question: {},
          insights: {},
        };

        const defaultOptions = {};
        const dynamicOptions = {};

        dropdowns.forEach((d) => {
          initialSelected.user[d.field] = d.type === "number"
            ? { start: "", end: "" }
            : [];

          initialSelected.question[d.field] = d.type === "number"
            ? { start: "", end: "" }
            : [];

          initialSelected.insights[d.field] = d.type === "number"
            ? { start: "", end: "" }
            : [];

          defaultOptions[d.field] = d.options;
          dynamicOptions[d.field] = [];
        });

        setSelectedValues(initialSelected);
        setDefaultOptionsForFields(defaultOptions);
        setDynamicOptionsForFields(dynamicOptions);
        setColumnData(dropdowns);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
  }, []);

  const fetchOptions = useCallback(
    debounce((inputValue, fieldName) => {
      if (!inputValue) {
        // Reset to default options when search is cleared
        setDynamicOptionsForFields(prev => ({
          ...prev,
          [fieldName]: []
        }));
        return;
      }

      setLoadingForFields((prev) => ({ ...prev, [fieldName]: true }));

      axios
        .get(url.searchUsersByMetaData, {
          params: {
            term: inputValue,
            type: 'triviaResult',
            key: fieldName,
          },
        })
        .then(({ data }) => {
          const newOptions =
            data && data.results
              ? data.results.map((item) => ({
                label: item.value,
                value: item.value,
              }))
              : [];

          setDynamicOptionsForFields((prev) => ({
            ...prev,
            [fieldName]: newOptions
          }));
        })
        .catch(() => {
          setDynamicOptionsForFields((prev) => ({
            ...prev,
            [fieldName]: []
          }));
        })
        .finally(() => {
          setLoadingForFields((prev) => ({ ...prev, [fieldName]: false }));
        });
    }, 500),
    [type]
  );

  const handleSelectChange = (field, selectedOptions) => {
    const optionsArray = Array.isArray(selectedOptions)
      ? selectedOptions
      : selectedOptions
        ? [selectedOptions]
        : [];

    const values = optionsArray.map((opt) => opt && opt.value);

    setSelectedValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: values,
      },
    }));
  };

  const handleDateChange = (field, value) => {
    const items = value.map((x) => new Date(x));
    setSelectedValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: items,
      },
    }));
  };

  const handleNumberChange = (field, which, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: {
          ...(prev[type][field] || { start: "", end: "" }),
          [which]: value,
        },
      },
    }));
  };

  const handleApply = () => {
    // Clone the object to avoid mutating state directly
    const valuesToApply = { ...selectedValues[type] };

    // Add sentiment and needsAttention filters for insights and user tab
    if (type === "insights" || type === "user") {
      valuesToApply.sentiment = sentiment.value !== "ALL" ? [sentiment.value] : [];
      valuesToApply.needsAttention = needsAttention ? ["true"] : [];
    }

    setFilterResultsValues(valuesToApply || {}, typesArray);
    setLastAppliedValues((prev) => ({
      ...prev,
      [type]: valuesToApply,
    }));
  };

  const handleClear = () => {
    const cleared = {};
    columnData.forEach((col) => {
      cleared[col.field] = col.type === "number"
        ? { start: "", end: "" }
        : [];
    });

    // Reset sentiment and needsAttention filters
    setSentiment({ label: "All", value: "ALL" });
    setNeedsAttention(false);

    setSelectedValues((prev) => ({
      ...prev,
      [type]: cleared,
    }));

    setLastAppliedValues((prev) => ({
      ...prev,
      [type]: cleared,
    }));

    setClearFilters();
  };

  const checkExtraFiltersEqual = () => {
    if (type !== "insights" && type !== "user") return true;

    // Check Sentiment
    const currentSentVal = sentiment.value !== "ALL" ? sentiment.value : null;
    const lastSentArr = (lastAppliedValues[type] && lastAppliedValues[type].sentiment) || [];
    const lastSentVal = lastSentArr.length > 0 ? lastSentArr[0] : null;

    if (currentSentVal !== lastSentVal) return false;

    // Check Needs Attention
    const currentNeedAtt = needsAttention;
    const lastNeedAttArr = (lastAppliedValues[type] && lastAppliedValues[type].needsAttention) || [];
    const lastNeedAtt = lastNeedAttArr.length > 0 && lastNeedAttArr[0] === "true";

    if (currentNeedAtt !== lastNeedAtt) return false;

    return true;
  };

  const getCurrentSelectedValues = (field) => {
    return selectedValues[type] && selectedValues[type][field]
      ? selectedValues[type][field]
      : [];
  };

  const hasSelectedValues = useMemo(() => {
    const current = selectedValues[type] || {};
    const hasRegularFilters = Object.keys(current).some((key) => {
      const val = current[key];
      if (Array.isArray(val)) return val.length > 0;
      if (val && typeof val === "object")
        return val.start !== "" || val.end !== "";
      return false;
    });

    // Check sentiment and needsAttention for insights and user type
    if (type === "insights" || type === "user") {
      return hasRegularFilters || sentiment.value !== "ALL" || needsAttention;
    }

    return hasRegularFilters;
  }, [selectedValues, type, sentiment, needsAttention]);

  const columnDataMap = useMemo(() => {
    const map = {};
    columnData.forEach((col) => {
      map[col.field] = col;
    });
    return map;
  }, [columnData]);



  function mergeFieldOptionsIntoColumnData(columnData, fieldOptions) {
    // Create a lookup for existing fields
    var existingFields = {};
    for (var i = 0; i < columnData.length; i++) {
      var field = columnData[i] && columnData[i].field;
      if (field) {
        existingFields[field] = true;
      }
    }

    // Collect new fields to add at the beginning
    var newFields = [];
    for (var j = 0; j < fieldOptions.length; j++) {
      var option = fieldOptions[j];
      var fieldName = option && option.value;
      if (fieldName && !existingFields[fieldName]) {
        newFields.push({
          field: fieldName,
          options: [],
          isActive: true,
          isValidFormat: false,
          type: "string",
        });
      }
    }

    // Prepend new fields to the original columnData
    return newFields.concat(columnData || []);
  }

  const allOptionsNew = mergeFieldOptionsIntoColumnData(columnData, fieldOptions);

  let showFilter = true

  if (currentTrivia && currentTrivia.totalSubmissions > 0) {
    let { allowAnonymousResponses, totalSubmissions } = currentTrivia
    if (allowAnonymousResponses) {
      if (totalSubmissions <= 5) {
        showFilter = true
      }
    }
  }

  // Shared between the desktop (top) and mobile (sticky footer) Apply buttons.
  const applyDisabled =
    !hasSelectedValues ||
    (areFiltersEqual(
      selectedValues[type],
      lastAppliedValues[type],
      columnDataMap
    ) &&
      checkExtraFiltersEqual());


  return (
    <div
      className={showFilter ? "filterResults" : "filterResults anonymousNoFilter"}
      style={{
        padding: "0px 30px 10px 44px",
      }}
    >
      <div
        style={{
          paddingTop: "15px",
          paddingBottom: "10px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2 filterHeader">
          <h5 className="mb-0">
            Filter Options {showFilter ? "" : " - too few responses to maintain anonymity"}
          </h5>
        </div>

        {!isMobile && allOptionsNew.length > 0 && (
          <div className="d-flex mt-3 filter-btn-row">
            <Button onClick={handleClear} color="danger" outline className="filter-reset-btn">
              Reset
            </Button>
            <Button
              onClick={handleApply}
              className="filter-apply-btn"
              disabled={applyDisabled}
            >
              Apply
            </Button>
          </div>
        )}
      </div>

      {/* Sentiment and Needs Attention filters - for insights and user tabs and NOT for Knowledge Checks */}
      {(type === "insights" || type === "user") && currentTrivia && currentTrivia.triviaCategoryType !== "KNOWLEDGE_CHECK" && (
        <div style={{ marginTop: "20px", paddingBottom: "15px", borderBottom: "1px solid #e0e0e0" }}>
          <FormGroup>
            <Label className="filterResultsLabel" style={{ color: hasTextQuestion ? undefined : "#aaa" }}>Sentiment</Label>
            <Select
              className="filterResultsSelect"
              value={sentiment}
              options={[
                { label: "All", value: "ALL" },
                { label: "Positive", value: "POSITIVE" },
                { label: "Neutral", value: "NEUTRAL" },
                { label: "Negative", value: "NEGATIVE" },
                { label: "Mixed", value: "MIXED" },
              ]}
              onChange={(option) => hasTextQuestion && setSentiment(option)}
              clearable={false}
              searchable={false}
              disabled={!hasTextQuestion}
              styles={!hasTextQuestion ? {
                control: (base) => ({ ...base, opacity: 0.45, pointerEvents: "none", cursor: "not-allowed", backgroundColor: "#f5f5f5" }),
              } : {}}
            />
          </FormGroup>

          <FormGroup>
            <Label className="filterResultsLabel" style={{ color: hasTextQuestion ? undefined : "#aaa" }}>
              Needs Attention
            </Label>
            <div
              className="filter-needs-attention-box"
              style={{
                opacity: hasTextQuestion ? 1 : 0.45,
                cursor: hasTextQuestion ? "pointer" : "not-allowed",
              }}
              onClick={() => hasTextQuestion && setNeedsAttention(!needsAttention)}
            >
              <div
                style={{
                  width: "40px",
                  height: "22px",
                  borderRadius: "11px",
                  backgroundColor: needsAttention && hasTextQuestion ? "#3b82f6" : "#ccc",
                  position: "relative",
                  transition: "background-color 0.3s",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    position: "absolute",
                    top: "2px",
                    left: needsAttention && hasTextQuestion ? "20px" : "2px",
                    transition: "left 0.3s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                />
              </div>
              <span className="filter-needs-attention-state">
                {needsAttention && hasTextQuestion ? "On" : "Off"}
              </span>
            </div>
          </FormGroup>
        </div>
      )}

      <div className="accordion-content-filter">
        {allOptionsNew.map(
          ({ field, isActive, isValidFormat, type: fieldType }) => {

            if (isActive === false) return null;

            const camelField = field;
            const currentSelected = getCurrentSelectedValues(field);

            const labelFilter = field
              .replace(/([a-z])([A-Z])/g, "$1 $2")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");

            // Combine default and dynamic options
            const defaultOptions = defaultOptionsForFields[field] || [];
            const dynamicOptions = dynamicOptionsForFields[field] || [];
            const allOptions = [...defaultOptions, ...dynamicOptions];

            // Remove duplicates
            const uniqueOptions = allOptions.filter(
              (option, index, self) =>
                index === self.findIndex((o) => o.value === option.value)
            );


            // Ensure selected values are always in options
            const selectedValuesArray = Array.isArray(currentSelected)
              ? currentSelected
              : [];


            let missingOptions = selectedValuesArray
              .filter(value =>
                !uniqueOptions.some(opt => opt.value === value));
            missingOptions = missingOptions && missingOptions.length > 0 ? missingOptions.map(value => ({ label: value, value: value })) : []


            const optionsToShow = [...uniqueOptions, ...missingOptions];

            // Get current selected options
            const selectedOptions = optionsToShow.filter(opt =>
              selectedValuesArray.includes(opt.value)
            );

            return (
              <FormGroup key={camelField}>
                <Label for={camelField} className="filterResultsLabel">
                  {labelFilter}
                </Label>

                {fieldType === "date" && isValidFormat && isActive ? (
                  <DatePickerMulti
                    value={currentSelected}
                    onChange={(value) => handleDateChange(field, value)}
                    range
                    style={{ height: "40px", width: "100%" }}
                    format="MM/DD/YYYY"
                    placeholder="Select Date Range"
                    arrow
                  />
                ) : fieldType === "number" ? (
                  <div style={{ display: "flex", gap: "5px" }}>
                    <input
                      type="number"
                      placeholder="Start"
                      value={
                        currentSelected && currentSelected.start
                          ? currentSelected.start
                          : ""
                      }
                      onChange={(e) =>
                        handleNumberChange(field, "start", e.target.value)
                      }
                      style={{
                        height: "40px",
                        width: "100%",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                    <input
                      type="number"
                      placeholder="End"
                      value={
                        currentSelected && currentSelected.end
                          ? currentSelected.end
                          : ""
                      }
                      onChange={(e) =>
                        handleNumberChange(field, "end", e.target.value)
                      }
                      style={{
                        height: "40px",
                        width: "100%",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                ) : (
                  <Select
                    className="filterResultsSelect"
                    name={camelField}
                    value={selectedOptions}
                    options={optionsToShow}
                    onSelectResetsInput={false}
                    onChange={(opts) => {
                      handleSelectChange(field, opts);
                    }}
                    onInputChange={(inputValue) => {
                      fetchOptions(inputValue, field);
                    }}
                    isLoading={loadingForFields[field] || false}
                    multi
                    clearable={true}
                    placeholder="Search..."
                  />
                )}
              </FormGroup>
            );
          }
        )}
      </div>

      {/* Mobile bottom-sheet footer: Reset / Apply pinned at the bottom of the
          sheet (design spec). Desktop keeps the top button row above. */}
      {isMobile && allOptionsNew.length > 0 && (
        <div className="filter-btn-row filter-btn-row-footer">
          <Button onClick={handleClear} color="danger" outline className="filter-reset-btn">
            Reset
          </Button>
          <Button
            onClick={handleApply}
            className="filter-apply-btn"
            disabled={applyDisabled}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterResults;